'use client';

import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { IoStorefront } from 'react-icons/io5';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ResponsiveDialogProvider } from '@eliadmin/core/src/components/ui/responsive-dialog';
import { lazy, Suspense, useState } from 'react';

import { FaLightbulb } from 'react-icons/fa';
import { Modal } from '@eliadmin/core/src/components/ui/modal';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';

const ProductAskSupplierDialog = lazy(() =>
  import('@eliadmin/core/src/components/product/detail/product-ask-supplier-dialog').then(
    module => ({
      default: module.ProductAskSupplierDialog,
    }),
  ),
);

interface ProductDetailAvailabilityProps {
  inDetail?: boolean;
}

export function ProductDetailAvailabilityInner({
  inDetail = false,
}: ProductDetailAvailabilityProps) {
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const product = useVariantsStore(state => state.product);
  const { t } = useTranslations();
  const [inquiryOpen, setInquiryOpen] = useState(false);

  const hasVariants = (product?.variants?.length ?? 0) > 0;
  const activeItem = hasVariants ? (selectedVariant ?? product) : product;

  const availability = selectedVariant?.availability ?? product?.availability;

  const isAvailable = availability?.id === 1;

  if (isAvailable)
    return (
      <div className="flex w-full flex-col items-start justify-start gap-6 sm:flex-row sm:items-center">
        <div className={cn('flex items-center justify-start gap-2')}>
          <IoStorefront size={20} className="text-[#117F11]" />
          <div className="text-xs font-semibold text-[#117F11] sm:text-sm">
            {activeItem?.availability?.name}&nbsp;
            {activeItem?.quantity > 50 ? `50+` : `(${activeItem?.quantity})`}
            &nbsp;
            {product?.unit}
          </div>
        </div>
        {inDetail && (
          <Modal
            title={t('text1981')}
            showDefaultClose
            open={inquiryOpen}
            onOpenChange={setInquiryOpen}
            trigger={
              <Button
                size={'none'}
                variant={'none'}
                className={cn(
                  'text-[#117F11]',
                  'ea-product-detail-actions__ask-for-product-button',
                )}
              >
                <FaLightbulb
                  className={cn(
                    'h-5 w-5',
                    'ea-product-detail-actions__ask-for-product-button-icon',
                  )}
                />
                {t('text1981')}
              </Button>
            }
          >
            {inquiryOpen && (
              <Suspense fallback={<Skeleton className="h-48 w-full" />}>
                <ProductAskSupplierDialog title={product.name} />
              </Suspense>
            )}
          </Modal>
        )}
      </div>
    );
}

export const ProductDetailAvailability = ({ inDetail = false }: ProductDetailAvailabilityProps) => {
  if (!inDetail) return <ProductDetailAvailabilityInner />;
  return (
    <ResponsiveDialogProvider>
      <ProductDetailAvailabilityInner inDetail={inDetail} />
    </ResponsiveDialogProvider>
  );
};
